import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { receiptService } from '../../../services/ReceiptService';
import { 
  SearchIcon, 
  CurrencyDollarIcon,
  DocumentTextIcon,
  CalendarIcon,
  RefreshIcon,
  ReceiptRefundIcon,
  ExclamationCircleIcon
} from '@heroicons/react/outline';
import { CheckCircleIcon } from '@heroicons/react/solid';

const EOReceiptsSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [events, setEvents] = useState([]);

  const fetchReceipts = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await receiptService.getEventReceipts(user.uid);
      if (response.success) {
        const allReceipts = response.receipts || [];
        
        const uniqueEvents = [...new Set(allReceipts.map(r => r.eventId).filter(Boolean))];
        const eventDetails = uniqueEvents.map(eventId => ({
          id: eventId,
          name: allReceipts.find(r => r.eventId === eventId)?.eventName || 'Unknown Event'
        }));
        
        setEvents(eventDetails);
        setReceipts(allReceipts);
        setError(null);
      } else {
        setError(response.error || t('receipts.unableToLoad'));
        setReceipts([]);
      }
    } catch (err) {
      console.error('Error fetching receipts:', err);
      setError(t('marketplaceDetails.somethingWentWrong'));
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.uid) {
      fetchReceipts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.seconds 
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    const value = typeof amount === 'number' ? amount : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value / 100);
  };

  const filteredReceipts = receipts.filter(receipt => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (receipt.mpsBusinessName?.toLowerCase() || '').includes(searchLower) ||
      (receipt.businessName?.toLowerCase() || '').includes(searchLower) ||
      (receipt.eventName?.toLowerCase() || '').includes(searchLower);
    const matchesEvent = selectedEvent === 'all' || receipt.eventId === selectedEvent;
    return matchesSearch && matchesEvent;
  });

  const stats = {
    totalAmount: filteredReceipts.reduce((sum, r) => sum + (r.amount || r.totalAmount || 0), 0),
    totalReceipts: filteredReceipts.length
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="flex flex-col items-center justify-center py-12 transition-opacity duration-300">
          <p className="text-gray-500 text-sm">{t('receipts.loadingReceipts')}</p>
        </div>
      </div>
    );
  }

  if (error && receipts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mb-4">
            <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('receipts.unableToLoad')}</h3>
          <p className="text-sm text-gray-500 mb-4 text-center max-w-sm">{error}</p>
          <button
            onClick={fetchReceipts}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-pebble-red rounded-lg hover:bg-pebble-red/90 transition-colors"
          >
            <RefreshIcon className="h-4 w-4 mr-2" />
            {t('settings.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('receipts.vendorPaymentReceipts')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('receipts.trackPayments')}
            </p>
          </div>
          <button
            onClick={fetchReceipts}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <RefreshIcon className="h-4 w-4 mr-2" />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {receipts.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-4 p-6 bg-gray-50 border-b border-gray-100">
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{t('receipts.totalCollected')}</p>
                  <p className="text-lg font-semibold text-gray-900">{formatAmount(stats.totalAmount)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{t('receipts.totalReceipts')}</p>
                  <p className="text-lg font-semibold text-gray-900">{stats.totalReceipts}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <SearchIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('receipts.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pebble-red/20 focus:border-pebble-red"
                />
              </div>
              {events.length > 0 && (
                <select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pebble-red/20 bg-white min-w-[160px]"
                >
                  <option value="all">{t('common.all')} {t('nav.events')}</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>{event.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </>
      )}

      <div className="p-6">
        {filteredReceipts.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <ReceiptRefundIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || selectedEvent !== 'all' ? t('receipts.noMatchingReceipts') : t('receipts.noReceiptsYet')}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              {searchTerm || selectedEvent !== 'all'
                ? t('receipts.noReceiptsAdjust')
                : t('receipts.noReceiptsDesc')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredReceipts.map((receipt) => (
              <div 
                key={receipt.id} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {receipt.mpsBusinessName || receipt.businessName || 'Unknown Vendor'}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="truncate">{receipt.eventName || 'Unknown Event'}</span>
                      <span className="flex-shrink-0 flex items-center gap-1">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {formatDate(receipt.createdAt || receipt.paymentDate)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 text-right ml-4">
                  <p className="font-semibold text-gray-900">
                    {formatAmount(receipt.amount || receipt.totalAmount)}
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">{t('receipts.paid')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EOReceiptsSection;
